import { Injectable } from '@angular/core';

import {
  map,
  merge,
  Observable,
  of,
  pairwise,
  switchMap,
  tap,
  filter,
  take,
  startWith,
  combineLatest,
} from 'rxjs';

import { Service } from '@shared/service/models';
import { WebSocketEventsEnum } from '@shared/websocket';
import { ServiceApiService } from './service-api.service';
import {
  AuthRestaurantService,
  ServiceQuery,
  ServiceStore,
  SocketService,
} from '@tablesready/frontend-store';

@Injectable({ providedIn: 'root' })
export class ServicesService {
  constructor(
    private readonly apiClient: ServiceApiService,
    private readonly store: ServiceStore,
    private readonly query: ServiceQuery,
    private readonly socketService: SocketService,
    private readonly authRestaurantService: AuthRestaurantService,
  ) {
  }

  load(): Observable<Service[]> {
    return this.authRestaurantService.serviceSettings()
      .pipe(
        take(1),
        switchMap(settings => settings.enabled ? this.apiClient.getAll() : of([])),
        tap(services => this.store.set(services)),
      );
  }

  getById(id: string): Service {
    return this.query.getEntity(id);
  }

  selectAll(): Observable<Service[]> {
    return this.query.selectAll();
  }

  selectAllSorted(): Observable<Service[]> {
    return combineLatest([
      this.authRestaurantService.serviceSettings()
        .pipe(
          startWith(null),
          pairwise(),
          filter(([prev, current]) => !prev || prev?.sort !== current?.sort),
          map(([, settings]) => settings),
        ),
      this.selectAll(),
    ])
      .pipe(
        map(([settings, services]) => {
          const sortColumn = settings?.sort || 'order';

          return [...services].sort((a, b) => {
            if (a[sortColumn] < b[sortColumn]) {
              return -1;
            }

            if (a[sortColumn] > b[sortColumn]) {
              return 1;
            }

            return a._id < b._id ? -1 : 1;
          });
        }),
      );
  }

  selectAllSortedByName(): Observable<Service[]> {
    return this.selectAll().pipe(
      map(services => {
        return services.sort((service1, service2) => {
          if (service1.name === service2.name) {
            return 0;
          }

          return service1.name > service2.name ? 1 : -1;
        });
      })
    );
  }

  selectAllEnabled(): Observable<Service[]> {
    return this.selectAll()
      .pipe(map(services => services.filter(service => service.enabled)));
  }

  isEnabled(): Observable<boolean> {
    return combineLatest([
      this.authRestaurantService.serviceSettings(),
      this.selectAllEnabled(),
    ])
      .pipe(
        map(([{ enabled }, services]) => (enabled || false) && services.length > 0)
      );
  }

  getServicesByIds(ids: string[]): Service[] {
    return this.query.getAll({
      filterBy: [entity => ids.includes(entity._id)],
    });
  }

  getEnabledByIds(ids: string[] = []): Service[] {
    return this.query.getAll({
      filterBy: [entity => ids.includes(entity._id) && entity.enabled],
    });
  }

  listenToChanges() {
    return merge(
      this.listenToEnablingDisabling(),
      this.socketService.getEventStream<Service[]>(WebSocketEventsEnum.serviceCreated)
        .pipe(tap(services => this.store.add(services))),
      this.socketService.getEventStream<Service | Service[]>(WebSocketEventsEnum.serviceUpdated)
        .pipe(
          map(service => Array.isArray(service) ? service : [service]),
          tap(services => {
            for (let i = 0; i < services.length; i++) {
              this.store.update(services[i]._id, services[i]);
            }
          }),
        ),
      this.socketService.getEventStream<void>(WebSocketEventsEnum.serviceUpdatedBulk)
        .pipe(switchMap(() => this.load())),
      this.socketService.getEventStream<string[]>(WebSocketEventsEnum.serviceDeleted)
        .pipe(tap(ids => this.store.remove(ids))),
    );
  }

  reset() {
    this.store.reset();
  }

  private listenToEnablingDisabling() {
    return this.authRestaurantService.serviceSettings()
      .pipe(
        pairwise(),
        filter(([prev, current]) => prev.enabled !== current.enabled),
        switchMap(() => this.load()),
      )
  }
}
